import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Apple, Play } from "lucide-react";
import { PHOTOS } from "@/data/destinations";
import { useLang } from "@/i18n/LanguageContext";
import Reveal from "@/components/Reveal";

const Phone = ({ children, className = "", testId }) => (
  <div
    data-testid={testId}
    className={`relative aspect-[9/19] w-[240px] rounded-[2.6rem] border border-white/20 bg-[#0a1128] p-2.5 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)] ${className}`}
  >
    <div className="relative h-full w-full overflow-hidden rounded-[2rem] bg-[#050814]">
      <div className="absolute left-1/2 top-2 z-20 h-5 w-20 -translate-x-1/2 rounded-full bg-black" />
      {children}
    </div>
  </div>
);

export default function AppSection() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const y2 = useTransform(scrollYProgress, [0, 1], [110, -30]);

  return (
    <section id="app" ref={ref} className="relative overflow-hidden py-28 lg:py-40" data-testid="app-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_60%,#0e1838_0%,#050814_65%)]" />
      <div className="relative mx-auto grid max-w-[1500px] items-center gap-16 px-6 lg:grid-cols-2 lg:px-12">
        <div>
          <Reveal>
            <p className="eyebrow mb-6">{t("app.eyebrow")}</p>
            <h2 className="font-serif text-4xl font-light leading-tight text-white sm:text-5xl lg:text-6xl">
              {t("app.title")}
            </h2>
            <p className="mt-8 max-w-md text-base font-light leading-relaxed text-white/65">{t("app.sub")}</p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap gap-4">
              <button data-testid="store-ios" className="btn-outline-gold flex items-center gap-3 px-6 py-3.5">
                <Apple size={22} />
                <span className="text-left">
                  <span className="block font-mono text-[9px] uppercase tracking-[0.2em] opacity-70">iOS</span>
                  <span className="block text-sm font-semibold">{t("app.ios")}</span>
                </span>
              </button>
              <button data-testid="store-android" className="btn-outline-gold flex items-center gap-3 px-6 py-3.5">
                <Play size={20} />
                <span className="text-left">
                  <span className="block font-mono text-[9px] uppercase tracking-[0.2em] opacity-70">Android</span>
                  <span className="block text-sm font-semibold">{t("app.android")}</span>
                </span>
              </button>
            </div>
            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-white/35" data-testid="app-soon-note">
              {t("app.soon")}
            </p>
          </Reveal>
        </div>

        <div className="relative flex h-[520px] items-center justify-center">
          <motion.div style={{ y: y2 }} className="absolute left-2 top-10 hidden rotate-[-8deg] opacity-80 sm:block lg:left-10">
            <Phone testId="app-mockup-android">
              <div className="flex h-full flex-col gap-3 p-4 pt-12">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    data-testid={`app-screenshot-placeholder-${i}`}
                    className="flex flex-1 items-center justify-center border border-dashed border-white/20 bg-white/[0.03]"
                  >
                    <span className="px-2 text-center font-mono text-[9px] uppercase tracking-[0.2em] text-white/35">
                      {t("app.placeholder")}
                    </span>
                  </div>
                ))}
              </div>
            </Phone>
          </motion.div>

          <motion.div style={{ y: y1 }} className="relative z-10 rotate-[4deg]">
            <Phone testId="app-mockup-iphone">
              <img src={PHOTOS.singapore} alt="App preview" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-b from-[#050814]/70 via-transparent to-[#050814]/90" />
              <div className="absolute inset-x-4 top-12 z-10 border border-white/20 bg-[#050814]/70 px-4 py-2.5 font-mono text-[10px] tracking-[0.2em] text-white/70 backdrop-blur-md">
                SINGAPORE · SIN
              </div>
              <div className="absolute inset-x-4 bottom-6 z-10 space-y-2">
                <div className="border border-[#d4af37]/40 bg-[#0a1128]/85 p-3 backdrop-blur-md">
                  <div className="h-1.5 w-2/3 bg-white/25" />
                  <div className="mt-2 h-1.5 w-1/2 bg-white/15" />
                  <div className="mt-3 h-6 w-24 bg-[#d4af37]" />
                </div>
              </div>
            </Phone>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
